import { useCallback } from "react";
import { update } from "../../services/DataService";

/**
 * Centralizes "Assign to me" and "Assignee committed" logic for CustomerChatView.
 */
export default function AssigneeActions ({
    chats, selected, setChats, setSelected, fetchChats,
    assigneeOpenChatId, setAssigneeOpenChatId,
    currentUserName, currentRepId,
}) {
    // Assign the currently opened popover row to me
    const onAssignToMe = useCallback(async () => {
        const row = chats.find(c => c.ChatID === assigneeOpenChatId);
        if (!row) return;

        // optimistic list patch
        setChats(list => list.map(c =>
            c.ChatID === row.ChatID
                ? {
                    ...c,
                    RepresentativeAssigneeName: currentUserName,
                    RepresentativeAssigneeID: currentRepId ?? c.RepresentativeAssigneeID,
                    TeamAssigneeName: null
                }
                : c
        ));

        // optimistic RHS patch (if selected is same row)
        if (selected?.ChatID === row.ChatID) {
            setSelected(s => s ? {
                ...s,
                RepresentativeAssigneeName: currentUserName,
                RepresentativeAssigneeID: currentRepId ?? s.RepresentativeAssigneeID,
                TeamAssigneeName: null
            } : s);
        }

        try {
            const ok = await update("Chat_SetAssignee", { ChatId: row.ChatID, AssignToCurrent: true });
            if (!ok) throw new Error("server returned false");
        } catch {
            await fetchChats(undefined); // self-heal on failure
        } finally {
            setAssigneeOpenChatId(null);
        }
    }, [chats, assigneeOpenChatId, currentUserName, currentRepId, selected, setChats, setSelected, fetchChats, setAssigneeOpenChatId]);

    // Commit a picker selection for either Team or User
    const onAssigneeCommitted = useCallback(({ kind, name }) => {
        const patchFor = (chat) => {
            if (kind === "Team") {
                return { ...chat, TeamAssigneeName: name, RepresentativeAssigneeName: null, RepresentativeAssigneeID: null };
            }
            // User
            const keepId = (currentUserName && name === currentUserName && currentRepId != null)
                ? Number(currentRepId)
                : chat.RepresentativeAssigneeID;
            return { ...chat, RepresentativeAssigneeName: name, TeamAssigneeName: null, RepresentativeAssigneeID: keepId };
        };

        setAssigneeOpenChatId(null);

        // optimistic list patch
        setChats(list => list.map(c =>
            // prioritize selected chat, otherwise patch the popover anchor row
            c.ChatID === (selected?.ChatID ?? assigneeOpenChatId) ? patchFor(c)
                : c.ChatID === assigneeOpenChatId ? patchFor(c)
                    : c
        ));

        // optimistic RHS patch
        if (selected?.ChatID) {
            setSelected(s => s ? patchFor(s) : s);
        }

        // refresh from server
        fetchChats(undefined);
    }, [assigneeOpenChatId, currentRepId, currentUserName, selected, setAssigneeOpenChatId, setChats, setSelected, fetchChats]);

    return { onAssignToMe, onAssigneeCommitted };
}
